/**
 * Content based file type detection.
 *
 * `File.type` is derived by the browser from the filename extension, so it is
 * absent for extensionless files and wrong for renamed ones. Everything here
 * looks at the actual leading bytes instead.
 */

export interface IFileSignature {
  /** canonical mime type */
  mime: string;
  /** canonical extension, including the dot */
  ext: string;
  /** short human readable label, used in error messages */
  label: string;
  /** matches against the first HEAD_BYTES bytes of the file */
  match: (head: Uint8Array) => boolean;
}

/** Enough for every signature below (HEIF needs 12). */
export const HEAD_BYTES = 16;

/** byte-sequence match at a fixed offset */
function at(offset: number, bytes: number[]): (head: Uint8Array) => boolean {
  return (head) => bytes.every((b, i) => head[offset + i] === b);
}

/** ascii-string match at a fixed offset */
function ascii(offset: number, text: string): (head: Uint8Array) => boolean {
  return at(offset, Array.from(text, (c) => c.charCodeAt(0)));
}

/** ISO-BMFF ('ftyp' box) brand match — HEIC, HEIF and AVIF share this layout */
function brand(...brands: string[]): (head: Uint8Array) => boolean {
  const isFtyp = ascii(4, 'ftyp');
  return (head) => isFtyp(head) && brands.some((b) => ascii(8, b)(head));
}

/**
 * Order matters: the first match wins, so the more specific ISO-BMFF brands
 * come before anything that could match loosely.
 */
export const FILE_SIGNATURES: IFileSignature[] = [
  {mime: 'application/pdf', ext: '.pdf', label: 'PDF', match: ascii(0, '%PDF-')},
  {mime: 'image/jpeg', ext: '.jpg', label: 'JPG', match: at(0, [0xff, 0xd8, 0xff])},
  {
    mime: 'image/png',
    ext: '.png',
    label: 'PNG',
    match: at(0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
  },
  {mime: 'image/gif', ext: '.gif', label: 'GIF', match: ascii(0, 'GIF8')},
  {
    mime: 'image/webp',
    ext: '.webp',
    label: 'WebP',
    match: (head) => ascii(0, 'RIFF')(head) && ascii(8, 'WEBP')(head),
  },
  {mime: 'image/avif', ext: '.avif', label: 'AVIF', match: brand('avif', 'avis')},
  {
    mime: 'image/heic',
    ext: '.heic',
    label: 'HEIC',
    match: brand('heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs'),
  },
  {mime: 'image/heif', ext: '.heif', label: 'HEIF', match: brand('mif1', 'msf1')},
  {
    mime: 'image/tiff',
    ext: '.tiff',
    label: 'TIFF',
    match: (head) => at(0, [0x49, 0x49, 0x2a, 0x00])(head) || at(0, [0x4d, 0x4d, 0x00, 0x2a])(head),
  },
  {mime: 'image/bmp', ext: '.bmp', label: 'BMP', match: ascii(0, 'BM')},
];

/**
 * Reads the leading bytes of `file` and returns the matching signature, or
 * `null` when the content matches none of them.
 *
 * This is a separate, small read so it works regardless of the `readAs` mode
 * the wrapper is configured with.
 */
export async function detectFileSignature(file: Blob): Promise<IFileSignature | null> {
  const head = new Uint8Array(await file.slice(0, HEAD_BYTES).arrayBuffer());
  return FILE_SIGNATURES.find((signature) => signature.match(head)) ?? null;
}

/** Splits an `accept` attribute into its tokens, lowercased. */
export function parseAccept(accept: string): string[] {
  return (accept ?? '')
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter((token) => token.length > 0);
}

/** Does `mime` (and optionally its extension) satisfy one of the accept tokens? */
export function matchesAccept(tokens: string[], mime: string, ext?: string): boolean {
  if (tokens.length === 0) return true;
  const lowerMime = (mime ?? '').toLowerCase();
  const lowerExt = (ext ?? '').toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith('.')) return token === lowerExt;
    if (token.endsWith('/*')) return lowerMime.startsWith(token.slice(0, -1));
    if (token === '*/*') return true;
    return token === lowerMime;
  });
}

/**
 * True when every accept token names something we can recognise by content.
 * If it does not (e.g. `text/plain`, which has no magic bytes), an undetected
 * file must not be rejected on content grounds alone.
 */
export function acceptIsFullySniffable(tokens: string[]): boolean {
  if (tokens.length === 0) return false;
  return tokens.every((token) =>
    FILE_SIGNATURES.some((signature) => matchesAccept([token], signature.mime, signature.ext))
  );
}

/** Human readable list of the accepted formats, for error messages. */
export function describeAccept(tokens: string[]): string {
  const labels = FILE_SIGNATURES.filter((signature) =>
    matchesAccept(tokens, signature.mime, signature.ext)
  ).map((signature) => signature.label);
  return Array.from(new Set(labels)).join(', ');
}
